import { useEffect, useMemo, useState } from "react";
import { Plus, ChevronLeft, Send, Pause, Play, Archive, Pencil, Eye, ImagePlus, Trash2 } from "lucide-react";
import client, { extractError } from "@/api";

const LISTING_TYPES = [
  { value: "PRODUCT", label: "Product" },
  { value: "CAPACITY", label: "Capacity" },
  { value: "SERVICE", label: "Service" },
];

const APPROVAL_LABEL = { DRAFT: "Draft", PENDING_REVIEW: "Pending review", APPROVED: "Approved", REJECTED: "Needs changes" };
const OPERATIONAL_LABEL = { ACTIVE: "Active", PAUSED: "Paused", ARCHIVED: "Archived", SOLD_OUT: "Sold out", UNAVAILABLE: "Unavailable" };

const APPROVAL_TABS = [
  { value: "", label: "All" },
  { value: "DRAFT", label: "Drafts" },
  { value: "PENDING_REVIEW", label: "Pending review" },
  { value: "APPROVED", label: "Approved" },
  { value: "REJECTED", label: "Rejected" },
];

function emptyForm() {
  return {
    listing_type: "PRODUCT", title: "", short_description: "", detailed_description: "",
    category_id: "", subcategory_id: "", state_id: "", city_id: "",
    area: "", pin_code: "", quantity: "", quantity_unit: "",
    indicative_value: 0, minimum_trade_value: "", attributes: {},
    availability_notes: "", available_from: "", available_until: "",
  };
}

function fromListing(l) {
  return {
    listing_type: l.listing_type, title: l.title,
    short_description: l.short_description, detailed_description: l.detailed_description,
    category_id: l.category_id, subcategory_id: l.subcategory_id || "",
    state_id: l.state_id, city_id: l.city_id,
    area: l.area || "", pin_code: l.pin_code || "",
    quantity: l.quantity ?? "", quantity_unit: l.quantity_unit || "",
    indicative_value: l.indicative_value, minimum_trade_value: l.minimum_trade_value ?? "",
    attributes: l.attributes || {},
    availability_notes: l.availability_notes || "",
    available_from: l.available_from ? l.available_from.slice(0, 16) : "",
    available_until: l.available_until ? l.available_until.slice(0, 16) : "",
  };
}

function ListingForm({ existing, references, onCancel, onSaved }) {
  const [form, setForm] = useState(existing ? fromListing(existing) : emptyForm());
  const [cities, setCities] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!form.state_id) return;
    client.get(`/reference/cities?state_id=${form.state_id}`).then((r) => setCities(r.data)).catch(() => setCities([]));
  }, [form.state_id]);

  const change = (k) => (e) => setForm((p) => {
    const next = { ...p, [k]: e.target.value };
    if (k === "state_id") next.city_id = "";
    if (k === "category_id") next.subcategory_id = "";
    return next;
  });

  const activeCategory = references.categories.find((c) => c.id === form.category_id);
  const typeLabel = LISTING_TYPES.find((t) => t.value === form.listing_type)?.label || "Offer";

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const payload = {
        ...form,
        subcategory_id: form.subcategory_id || null,
        quantity: form.quantity !== "" ? Number(form.quantity) : null,
        indicative_value: Number(form.indicative_value || 0),
        minimum_trade_value: form.minimum_trade_value !== "" ? Number(form.minimum_trade_value) : null,
        area: form.area || null,
        pin_code: form.pin_code || null,
        availability_notes: form.availability_notes || null,
        available_from: form.available_from ? new Date(form.available_from).toISOString() : null,
        available_until: form.available_until ? new Date(form.available_until).toISOString() : null,
      };
      const res = existing
        ? await client.patch(`/listings/${existing.id}`, payload)
        : await client.post("/listings", payload);
      onSaved(res.data);
    } catch (err) {
      setError(extractError(err, "Unable to save listing."));
    } finally { setBusy(false); }
  };

  return (
    <form className="company-form" onSubmit={submit} data-testid="listing-form">
      <button type="button" className="link-button back" data-testid="listing-form-back" onClick={onCancel}><ChevronLeft size={14} /> Back</button>
      <fieldset><legend>BASIC INFORMATION</legend>
        <div className="grid-2">
          <label>Listing type *
            <select required value={form.listing_type} onChange={change("listing_type")} data-testid="listing-type">
              {LISTING_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </label>
          <label>Title *<input required minLength={3} value={form.title} onChange={change("title")} data-testid="listing-title" placeholder={`${typeLabel} title`} /></label>
          <label>Category *
            <select required value={form.category_id} onChange={change("category_id")} data-testid="listing-category">
              <option value="">Select category…</option>
              {references.categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </label>
          <label>Subcategory
            <select value={form.subcategory_id} onChange={change("subcategory_id")} disabled={!activeCategory} data-testid="listing-subcategory">
              <option value="">Optional</option>
              {activeCategory?.subcategories?.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </label>
        </div>
        <label>Short description *<textarea required minLength={10} rows={2} maxLength={500} value={form.short_description} onChange={change("short_description")} data-testid="listing-short" placeholder="One-line summary shown on marketplace cards" /></label>
        <label>Detailed description *<textarea required minLength={10} rows={5} value={form.detailed_description} onChange={change("detailed_description")} data-testid="listing-detailed" /></label>
      </fieldset>

      <fieldset><legend>OFFER DETAILS</legend>
        <div className="grid-2">
          <label>Quantity<input type="number" min="0" value={form.quantity} onChange={change("quantity")} data-testid="listing-quantity" /></label>
          <label>Unit<input value={form.quantity_unit} onChange={change("quantity_unit")} data-testid="listing-unit" placeholder={form.listing_type === "PRODUCT" ? "kg / pcs / boxes" : form.listing_type === "CAPACITY" ? "hours / sqft / tonnes" : "hours / retainers"} /></label>
          <label>Indicative value (₹) *<input required type="number" min="0" value={form.indicative_value} onChange={change("indicative_value")} data-testid="listing-value" /></label>
          <label>Minimum trade value (₹)<input type="number" min="0" value={form.minimum_trade_value} onChange={change("minimum_trade_value")} data-testid="listing-min-value" /></label>
        </div>
        <label>Notes / attributes (informational)<textarea rows={2} value={form.availability_notes} onChange={change("availability_notes")} data-testid="listing-notes" /></label>
      </fieldset>

      <fieldset><legend>LOCATION</legend>
        <div className="grid-2">
          <label>State *
            <select required value={form.state_id} onChange={change("state_id")} data-testid="listing-state">
              <option value="">Select state…</option>
              {references.states.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </label>
          <label>City *
            <select required value={form.city_id} onChange={change("city_id")} disabled={!form.state_id} data-testid="listing-city">
              <option value="">{form.state_id ? "Select city…" : "Choose state first"}</option>
              {cities.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </label>
          <label>Area / locality<input value={form.area} onChange={change("area")} data-testid="listing-area" /></label>
          <label>PIN code<input value={form.pin_code} onChange={change("pin_code")} maxLength={6} data-testid="listing-pin" /></label>
        </div>
      </fieldset>

      <fieldset><legend>AVAILABILITY</legend>
        <div className="grid-2">
          <label>Available from<input type="datetime-local" value={form.available_from} onChange={change("available_from")} data-testid="listing-avail-from" /></label>
          <label>Available until<input type="datetime-local" value={form.available_until} onChange={change("available_until")} data-testid="listing-avail-until" /></label>
        </div>
      </fieldset>

      {error && <div className="form-error">{error}</div>}
      <div className="form-actions">
        <button type="button" className="ghost-button" onClick={onCancel} data-testid="listing-form-cancel">Cancel</button>
        <button type="submit" className="primary-button" disabled={busy} data-testid="listing-form-save">{busy ? "Saving…" : existing ? "Save changes" : "Save as draft"}</button>
      </div>
    </form>
  );
}

function StatusChips({ listing }) {
  return (
    <>
      <span className={`status-chip status-${listing.approval_status.toLowerCase()}`}>{APPROVAL_LABEL[listing.approval_status] || listing.approval_status}</span>
      <span className={`status-chip status-${listing.operational_status.toLowerCase()}`}>{OPERATIONAL_LABEL[listing.operational_status] || listing.operational_status}</span>
    </>
  );
}

function ListingDetail({ listing, references, onBack, onChanged }) {
  const [state, setState] = useState(listing);
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);

  const reload = async () => {
    const r = await client.get(`/listings/mine/${state.id}`);
    setState(r.data); onChanged?.(r.data);
  };

  const doAction = async (action, testMsg) => {
    setBusy(true); setError(""); setMessage("");
    try {
      await client.post(`/listings/${state.id}/${action}`);
      setMessage(testMsg);
      await reload();
    } catch (err) { setError(extractError(err)); } finally { setBusy(false); }
  };

  const uploadImage = async (event) => {
    const file = event.target.files?.[0]; event.target.value = "";
    if (!file) return;
    setUploading(true); setError("");
    try {
      const data = new FormData(); data.append("file", file);
      await client.post(`/listings/${state.id}/media?media_type=IMAGE`, data);
      await reload();
    } catch (err) { setError(extractError(err)); } finally { setUploading(false); }
  };

  const removeMedia = async (mediaId) => {
    if (!window.confirm("Remove this image?")) return;
    try { await client.delete(`/listings/${state.id}/media/${mediaId}`); await reload(); }
    catch (err) { setError(extractError(err)); }
  };

  const viewMedia = async (m) => {
    try {
      const res = await client.get(`/listings/${state.id}/media/${m.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank", "noopener");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) { setError(extractError(err)); }
  };

  if (editing) {
    return <ListingForm existing={state} references={references} onCancel={() => setEditing(false)} onSaved={(l) => { setState(l); setEditing(false); onChanged?.(l); }} />;
  }

  const canEdit = ["DRAFT", "REJECTED", "APPROVED"].includes(state.approval_status) && state.operational_status !== "ARCHIVED";
  const canSubmit = ["DRAFT", "REJECTED"].includes(state.approval_status);
  const canPause = state.approval_status === "APPROVED" && state.operational_status === "ACTIVE";
  const canResume = state.approval_status === "APPROVED" && state.operational_status === "PAUSED";
  const canArchive = state.operational_status !== "ARCHIVED";

  return (
    <div className="admin-company-detail" data-testid="listing-detail">
      <button type="button" className="link-button back" data-testid="listing-detail-back" onClick={onBack}><ChevronLeft size={14} /> My listings</button>
      <header className="detail-header">
        <div>
          <p className="eyebrow">{LISTING_TYPES.find((t) => t.value === state.listing_type)?.label?.toUpperCase() || state.listing_type}</p>
          <h2>{state.title}</h2>
          <p className="muted">{state.short_description}</p>
        </div>
        <div className="chip-stack"><StatusChips listing={state} /></div>
      </header>
      {state.approval_status === "REJECTED" && state.rejection_reason && (
        <div className="review-callout rejected"><p className="eyebrow">REJECTION REASON</p><p>{state.rejection_reason}</p></div>
      )}
      {message && <div className="form-success">{message}</div>}
      {error && <div className="form-error">{error}</div>}
      <div className="detail-actions">
        {canEdit && <button type="button" className="ghost-button" data-testid="listing-edit-btn" onClick={() => setEditing(true)}><Pencil size={14} /> Edit</button>}
        {canSubmit && <button type="button" className="primary-button" data-testid="listing-submit-btn" onClick={() => doAction("submit", "Submitted for review.")} disabled={busy}><Send size={14} /> Submit for approval</button>}
        {canPause && <button type="button" className="ghost-button" data-testid="listing-pause-btn" onClick={() => doAction("pause", "Paused.")} disabled={busy}><Pause size={14} /> Pause</button>}
        {canResume && <button type="button" className="ghost-button" data-testid="listing-resume-btn" onClick={() => doAction("resume", "Resumed.")} disabled={busy}><Play size={14} /> Resume</button>}
        {canArchive && <button type="button" className="ghost-button" data-testid="listing-archive-btn" onClick={() => doAction("archive", "Archived.")} disabled={busy}><Archive size={14} /> Archive</button>}
      </div>

      <section className="detail-grid">
        <article><p className="eyebrow">CATEGORY</p><p>{references.categories.find((c) => c.id === state.category_id)?.name || "—"}</p></article>
        <article><p className="eyebrow">LOCATION</p><p>{state.area}, {references.states.find((s) => s.id === state.state_id)?.name || ""} — {state.pin_code || "—"}</p></article>
        <article><p className="eyebrow">QUANTITY</p><p>{state.quantity ?? "—"} {state.quantity_unit || ""}</p></article>
        <article><p className="eyebrow">INDICATIVE VALUE</p><p>₹{state.indicative_value.toLocaleString("en-IN")}</p></article>
        <article><p className="eyebrow">MIN TRADE VALUE</p><p>{state.minimum_trade_value ? `₹${state.minimum_trade_value.toLocaleString("en-IN")}` : "—"}</p></article>
        <article className="wide"><p className="eyebrow">DETAILS</p><p>{state.detailed_description}</p></article>
      </section>

      <section className="documents-section">
        <div className="section-heading"><div><p className="eyebrow">MEDIA</p><h3>Images</h3></div>
          {canEdit && (() => {
            const imgCount = (state.media || []).filter((m) => m.media_type === "IMAGE" && !m.is_deleted).length;
            const atMax = imgCount >= 5;
            return (
              <label className={`ghost-button ${atMax ? "is-disabled" : ""}`} data-testid="listing-upload-media">
                {uploading ? "Uploading…" : (<><ImagePlus size={14} /> {atMax ? "Max 5 reached" : "Upload image"}</>)}
                <input type="file" accept=".png,.jpg,.jpeg" hidden onChange={uploadImage} disabled={uploading || atMax} data-testid="listing-image-input" />
              </label>
            );
          })()}
        </div>
        <p className="muted small" data-testid="listing-image-guidance">
          Upload up to 5 images · Recommended 2000 × 2000 px (1:1) · JPG, JPEG or PNG · Max 5 MB each.
          <br/>First image will be used as the primary image.
        </p>
        <p className="muted small" data-testid="listing-image-counter">
          Images: {(state.media || []).filter((m) => m.media_type === "IMAGE" && !m.is_deleted).length}/5
        </p>
        <ul className="document-list">
          {(state.media || []).length === 0 && <li className="muted small">No media uploaded yet.</li>}
          {(state.media || []).map((m, idx) => (
            <li key={m.id} data-testid={`listing-media-${m.id}`}>
              <ImagePlus size={14} />
              <div>
                <strong>{m.media_type}</strong>
                {m.media_type === "IMAGE" && idx === 0 && <span className="chip" data-testid={`listing-media-primary-${m.id}`}> · Primary</span>}
                <small>{m.file_name} · {(m.file_size / 1024).toFixed(1)} KB</small>
              </div>
              <button type="button" className="link-button" onClick={() => viewMedia(m)}><Eye size={12} /> View</button>
              {canEdit && <button type="button" className="danger-button" onClick={() => removeMedia(m.id)}><Trash2 size={14} /></button>}
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}

export default function MyListingsPage({ user }) {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [mode, setMode] = useState("list"); // list | new | detail
  const [detail, setDetail] = useState(null);
  const [references, setReferences] = useState({ states: [], categories: [] });
  const [companyStatus, setCompanyStatus] = useState(null);
  const [companyReady, setCompanyReady] = useState(false);

  useEffect(() => {
    Promise.all([
      client.get("/reference/states"),
      client.get("/reference/categories"),
      client.get("/companies/me"),
    ]).then(([s, c, co]) => {
      setReferences({ states: s.data, categories: c.data });
      setCompanyStatus(co.data?.status || null);
    }).catch(() => {}).finally(() => setCompanyReady(true));
  }, []);

  const load = async () => {
    setLoading(true); setError("");
    try {
      const params = new URLSearchParams();
      if (filter) params.set("approval_status", filter);
      const res = await client.get(`/listings/mine?${params.toString()}`);
      setItems(res.data || []);
    } catch (err) { setError(extractError(err, "Unable to load listings.")); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const counts = useMemo(() => {
    const c = { total: items.length };
    items.forEach((l) => { c[l.approval_status] = (c[l.approval_status] || 0) + 1; });
    return c;
  }, [items]);

  if (mode === "new") {
    return <ListingForm references={references} onCancel={() => setMode("list")} onSaved={(l) => { setDetail(l); setMode("detail"); }} />;
  }
  if (mode === "detail" && detail) {
    return <ListingDetail listing={detail} references={references} onBack={() => { setMode("list"); load(); }} onChanged={setDetail} />;
  }

  if (companyReady && companyStatus !== "VERIFIED") {
    const label = companyStatus ? `Your current company status is ${companyStatus}.` : "Register your company from the Company tab to get started.";
    return (
      <div className="empty-state" data-testid="listings-blocked">
        <p><strong>Complete verification to create listings</strong></p>
        <p className="muted small">Only verified companies can create marketplace listings. {label}</p>
      </div>
    );
  }

  return (
    <div className="my-listings-page" data-testid="my-listings-page">
      <div className="admin-toolbar">
        <div className="filter-tabs" data-testid="listing-status-tabs">
          {APPROVAL_TABS.map((t) => (
            <button key={t.value || "all"} type="button" data-testid={`filter-${t.value || "all"}`} className={filter === t.value ? "active" : ""} onClick={() => setFilter(t.value)}>
              {t.label}{t.value && counts[t.value] > 0 && <span className="count">{counts[t.value]}</span>}
            </button>
          ))}
        </div>
        <button type="button" className="primary-button" data-testid="new-listing-button" onClick={() => setMode("new")}><Plus size={14} /> New listing</button>
      </div>
      {error && <div className="form-error">{error}</div>}
      {loading ? <div className="loading-block">Loading…</div> : (
        <table className="admin-table" data-testid="my-listings-table">
          <thead><tr><th>Title</th><th>Type</th><th>Location</th><th>Value</th><th>Approval</th><th>Operational</th><th>Updated</th><th></th></tr></thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={8} className="empty-row">No listings yet — click <em>New listing</em> to create your first draft.</td></tr>}
            {items.map((l) => (
              <tr key={l.id} data-testid={`listing-row-${l.id}`}>
                <td><strong>{l.title}</strong><br /><small className="muted">{l.short_description}</small></td>
                <td>{LISTING_TYPES.find((t) => t.value === l.listing_type)?.label}</td>
                <td>{references.states.find((s) => s.id === l.state_id)?.name || "—"}</td>
                <td>₹{l.indicative_value.toLocaleString("en-IN")}</td>
                <td><span className={`status-chip status-${l.approval_status.toLowerCase()}`}>{APPROVAL_LABEL[l.approval_status]}</span></td>
                <td><span className={`status-chip status-${l.operational_status.toLowerCase()}`}>{OPERATIONAL_LABEL[l.operational_status]}</span></td>
                <td><small>{new Date(l.updated_at).toLocaleDateString()}</small></td>
                <td><button type="button" className="link-button" data-testid={`open-listing-${l.id}`} onClick={() => { setDetail(l); setMode("detail"); }}>Open →</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
